(ns frontend.ui.component.chart.view
  (:require
   [common.localization :as loc]
   [frontend.ui.component.chart.style :as style]
   [frontend.ui.component.lp-portal.modal.edit-modals.edit-chart-modal :as edit-chart-modal]
   [frontend.ui.page.lp-portal.report.util :as ru]
   [frontend.view.components.clipboard :as clipboard]
   ["highcharts" :as highcharts]
   ["highcharts/highcharts-more" :as highcharts-more]
   ["highcharts/highmaps" :as highmaps]
   ["highcharts/modules/exporting" :as exporting]
   ["highcharts/modules/map" :as high-map]
   ["highcharts/modules/annotations" :as annotations]
   [herb.core :as h]
   [potpuri.core :as potpuri]
   [reagent.core :as r] 
   [ui.components.chart.view :as chart]
   [ui.components.radix-ui.dropdown-menu.view :as dropdown-menu]))

(defn init-highcharts!
  []
  (high-map highcharts)
  (exporting highcharts)
  (highcharts-more highcharts)
  (exporting highmaps)
  (annotations highcharts)
  )

(def default-options
  {:credits   {:enabled false}
   ;; Don't show dummy placeholder saying "Chart title"
   :title     {:text nil}
   :tooltip   {:hideDelay 0}
   ;; Don't show top-right hand corner menu button to export charts
   :exporting {:enabled false}
   ;; Important for graphs exported to SVG for PDF
   :chart     {:backgroundColor "transparent"}})

(defn- with-defaults
  [conf]
  (-> default-options
      (potpuri/deep-merge conf)
      clj->js))

(defn- gen-chart
  ([dom-node config]
   (.chart highcharts dom-node (with-defaults config))))

(defn- gen-geomap
  ([dom-node config]
   (.mapChart highmaps dom-node (with-defaults config))))

(defn chart-svg
  [dom-node config]
  (let [svg (.getSVG (gen-chart dom-node config))]
    svg))

(defn geomap-svg
  [dom-node config]
  (.getSVG (gen-geomap dom-node config)))

(defn to-clipboard-image
  [config]
  (let [svg-string (chart-svg "highcharts" config)]
    (clipboard/svg-as-png svg-string)))

(defn geomap
  [config]
  (when config
    [chart/view
     (potpuri/deep-merge default-options
                         config)
     "mapChart"]))

(defn dataset-dropdown-items
  [previous-data-set-label
   imported-data-set-label
   manual-data-set-label
   selected-data-set-path
   selected-data-set
   save-text]
  (concat
   [(if previous-data-set-label
      {:label      previous-data-set-label
       :on-click   (fn []
                     (save-text selected-data-set-path (pr-str :previous-data-set)))
       :icon-class (h/join (if (= selected-data-set :previous-data-set)
                             "fas"
                             "fal")
                           "fa-circle")}
      {:label      (loc/tr :no-previous-report-data-available)
       :disabled?  true
       :icon-class "fal fa-circle"})
    (if imported-data-set-label
      {:label      imported-data-set-label
       :on-click   (fn []
                     (save-text selected-data-set-path (pr-str :imported-data-set)))
       :icon-class (h/join (if (= selected-data-set :imported-data-set)
                             "fas"
                             "fal")
                           "fa-circle")}
      {:label      (loc/tr :no-back-office-data-available)
       :disabled?  true
       :icon-class "fal fa-circle"})
    (if manual-data-set-label
      {:label      manual-data-set-label
       :on-click   (fn []
                     (save-text selected-data-set-path (pr-str :manual-data-set)))
       :icon-class (h/join (if (= selected-data-set :manual-data-set)
                             "fas"
                             "fal")
                           "fa-circle")}
      {:label      (loc/tr :no-manual-data-set-available)
       :disabled?  true
       :icon-class "fal fa-circle"})]))

(defn source-label
  [{:keys [selected-data-set previous-data-set-reference-date no-datasets?]}]
  (str (loc/tr :dataset-source)
       (if no-datasets?
         "No data"
         (case selected-data-set
           :manual-data-set
           (loc/tr :manual-data-set)
           :imported-data-set
           (loc/tr :imported-data-set)
           :previous-data-set
           (loc/tr :previous-data-set
                   [(when previous-data-set-reference-date
                      (ru/->date-dd-MM-yyyy previous-data-set-reference-date))])
           (loc/tr :no-data-set-selected)))))

(defn chart
  [_config _props]
  (let [local-state (r/atom {})]
    (fn
      [{:keys [previous-data-set-reference-date
               imported-data-set-label
               previous-data-set-label
               manual-data-set-label
               manual-data-set-path
               manual-chart-configs
               manual-chart-configs-path
               selected-data-set
               selected-data-set-path
               editable-data->raw-data
               chart-config->editable-data
               fixed-columns?
               fixed-labels?
               donut-chart?
               multi-chart?
               raw-data]
        :as   config}
       {:keys [save-text save-chart save-chart-configs chart-edit? chart-select?]}]
      (let [figures (or (:figures config)
                        config)
            chart-is-editable? (and (fn? chart-config->editable-data)
                                 (fn? editable-data->raw-data))
            chart-data-to-edit (if multi-chart?
                                 raw-data
                                 figures)
            no-datasets? (every? nil? [imported-data-set-label
                                       previous-data-set-label
                                       manual-data-set-label])]
        [:div
         {:class (h/<class style/chart-container)}
         (when (:show-data-modal? @local-state)
           [edit-chart-modal/view
            {:hide!          #(swap! local-state dissoc :show-data-modal?)
             :visible?       true
             :data           (chart-config->editable-data chart-data-to-edit)
             :local-state    local-state
             :save!          (fn [data]
                               (let [raw-data (editable-data->raw-data data)]
                                 (save-chart manual-data-set-path
                                             (pr-str raw-data)
                                             #(swap! local-state dissoc :show-data-modal?))))
             :fixed-columns? fixed-columns?
             :fixed-labels?  fixed-labels?
             :donut-chart? donut-chart?}])
         (when (:show-config-modal? @local-state)
           [edit-chart-modal/config-editor-view
            {:hide!          #(swap! local-state dissoc :show-config-modal?)
             :visible?       true
             :data           (or manual-chart-configs 
                                 (chart-config->editable-data chart-data-to-edit))
             :local-state    local-state
             :save!          (fn [data]
                               (let [data (if multi-chart?
                                            (assoc-in data [:series] (->> (:series data)
                                                                          (mapv (fn [series]
                                                                                 (dissoc series :data)))))
                                            data)]
                                 (save-chart-configs manual-chart-configs-path
                                                     (pr-str data)
                                                     #(swap! local-state dissoc :show-config-modal?))))
             :fixed-columns? fixed-columns?
             :fixed-labels?  fixed-labels?}])
           [:div
            {:class (h/<class style/chart-toolbar)}
            (when (and chart-edit?
                       chart-is-editable?
                       (not no-datasets?))
            [dropdown-menu/view
             {:trigger
              [:div
               {:class "mr-2"}
               [:i.fal.fa-edit]]
              :items (remove nil?
                             [{:label      "Edit chart data"
                               :on-click   #(swap! local-state assoc :show-data-modal? true)
                               :icon-class "fal fa-server"}
                              (when-not donut-chart?
                              {:label      "Edit chart configurations"
                               :on-click   #(swap! local-state assoc :show-config-modal? true)
                               :icon-class "fal fa-cog"})])}])
            (when (and chart-select?
                       chart-is-editable?)
              [dropdown-menu/view
               {:trigger-as-child
                [:div
                 {:class (h/<class style/dropdown-trigger)}
                 [:span
                  (source-label
                   {:selected-data-set selected-data-set
                    :previous-data-set-reference-date previous-data-set-reference-date
                    :no-datasets? no-datasets?})]
                 [:i.fal.fa-chevron-down]]
                :items (dataset-dropdown-items previous-data-set-label
                                               imported-data-set-label
                                               manual-data-set-label
                                               selected-data-set-path
                                               selected-data-set
                                               save-text)}])]
         (when figures
           [chart/view
            (potpuri/deep-merge default-options
                                figures)])]))))
